import { sameValues } from "@/lib/form";
import {
  CONTACT_SECTION, type Draft, draftOf, editable, parseDraft, refusalProblems, refusalText, shownSections,
  ownFields,
} from "@/lib/record-fields";
import type { RecordField, RecordSection } from "@/lib/types";

/**
 * The form a rep fills in during a call (the Phone page's form pane): the contact's own fields in
 * the call's three folders (`CALL_FORM_SECTIONS`), saved by themselves as the rep leaves each answer.
 *
 * Every decision is here, pure, for `test/call-form.test.ts`: what the pane says when there is no
 * form to draw, what one answer sends, what a refusal does to the answers that travelled with it,
 * and the words of the header and of the notice for answers left behind. The drafts and the queue
 * that apply these rules are `softphone/call-form-store.ts`; the pane is `softphone/call-form.tsx`.
 * What a value looks like, typed and sent, stays `lib/record-fields.ts`, shared with the contact
 * page.
 */

// --- which folders a call shows --------------------------------------------------------------------------

/**
 * The folders the call form draws, by key (ACL-558): Contact, Required Intake Questions and Full
 * Intake, and nothing else. A rep on a call is taking the intake, and Contact holds the screening
 * questions asked first (when, who, whose fault); the conversion, tracking, company and opportunity
 * folders are the record's, read and changed on the contact's page ("Open record"), and on the call
 * they were six more links in the header and a long scroll past answers nobody asks for while the lead
 * is talking. Contact's platform fields (the name, the phone, the e-mail) are not drawn here, as no
 * folder's are: the form's route does not save them, and the call's card already says who this is.
 *
 * Keys, because an admin may rename a folder but never change its key (`form-section-dialog.tsx`), and
 * every tenant's form is seeded with these three (`form_seed.py` in core-api). The admin's order is
 * kept. The contact's page and the Conversations panel are not narrowed: they are the record, not the
 * call.
 */
export const CALL_FORM_SECTIONS: readonly string[] = [CONTACT_SECTION, "required_intake_questions", "full_intake"];

/** The folders the call form draws: the call's, as far as each has a field of its own to draw. */
export function callFormSections(sections: readonly RecordSection[]): RecordSection[] {
  return shownSections(sections).filter((s) => CALL_FORM_SECTIONS.includes(s.key));
}

// --- what the pane says ----------------------------------------------------------------------------------

/**
 * The state of the pane when it is not simply a form:
 *  - `no_contact`: a campaign call whose caller has no contact yet;
 *  - `loading`: the first read is on its way;
 *  - `missing`: the server answered 404 (no such route yet, or a lead this rep may not open);
 *  - `failed`: any other failed read with nothing to show;
 *  - `empty`: no folder holds a field of its own;
 *  - `no_call_folders`: folders hold fields, but none of the call's does (`CALL_FORM_SECTIONS`);
 *  - `read_only`: folders are drawn, and this role may change none of their fields.
 */
export type FormNotice =
  | "no_contact" | "loading" | "missing" | "failed" | "empty" | "no_call_folders" | "read_only";

/** One muted line per state, in the words a rep reads. `loading` draws skeleton rows instead. */
export const FORM_NOTICE_TEXT: Record<Exclude<FormNotice, "loading">, string> = {
  no_contact: "This caller has no contact yet, so there is no form. Create the contact and the form opens here.",
  missing: "The form for this contact could not be opened.",
  failed: "The form could not be loaded.",
  empty: "No form has been set up yet. An admin adds the folders and fields.",
  no_call_folders:
    "This form has no Contact, Required Intake Questions or Full Intake folder. An admin can add them.",
  read_only:
    "You can read these answers but not change them. An admin chooses which folders your role can fill in.",
};

/**
 * Which of the states above the pane is in, or null when it draws the form and nothing else. A read
 * that fails while answers are on screen changes nothing: the rep keeps what they see, and the next
 * poll tries again.
 */
export function formNotice(state: {
  /** Whether the page has a lead for the form at all. */
  hasLead: boolean;
  /** The folders the server sent, or undefined until the first read answers. */
  sections: readonly RecordSection[] | undefined;
  /** Whether the read failed, and the status it failed with when the server said one. */
  failed: boolean;
  status: number | null;
}): FormNotice | null {
  if (!state.hasLead) return "no_contact";
  if (state.sections === undefined) {
    if (!state.failed) return "loading";
    return state.status === 404 ? "missing" : "failed";
  }
  if (shownSections(state.sections).length === 0) return "empty";
  const shown = callFormSections(state.sections);
  if (shown.length === 0) return "no_call_folders";
  if (!shown.some((s) => ownFields(s).some(editable))) return "read_only";
  return null;
}

/**
 * The folders the pane's header names, each a way down to that folder's answers: the ones the form
 * draws, in its order, and none while the pane says anything in place of the form (no contact,
 * the first read, a read that failed, an empty form). The header then says "Form" instead, so the
 * pane is never without a name. A form the role may only read still has its folders.
 */
export function formFolders(state: {
  hasLead: boolean;
  sections: readonly RecordSection[] | undefined;
}): RecordSection[] {
  if (!state.hasLead || state.sections === undefined) return [];
  return callFormSections(state.sections);
}

// --- one answer ------------------------------------------------------------------------------------------

/** An answer that has a row of the form's grid to itself (ACL-605): a long text, which wants a box
 *  of its own to be written and read in, and a list of ticks, which lays its options out across the
 *  row. Everything else is short and sits beside the answers next to it. The same whether the role
 *  may change it or only read it. */
export function takesRow(field: RecordField): boolean {
  return field.type === "long_text" || field.type === "multi_select";
}

/** The value an input shows: what the rep is typing if they are, else what the server holds. A
 *  refetch moves the second and never the first. */
export function shownDraft(field: RecordField, draft: Draft | undefined): Draft {
  return draft ?? draftOf(field);
}

/** A dropdown, a yes/no, a multi-select, a picked record and a day picked from the month are answered in
 *  one move, so they are sent as they change; anything typed is sent when the rep leaves the field. */
export function sentOnChange(field: RecordField): boolean {
  return (
    field.type === "dropdown" ||
    field.type === "boolean" ||
    field.type === "multi_select" ||
    field.type === "reference" ||
    field.type === "date"
  );
}

export const REQUIRED_PROBLEM = "Required: it cannot be left empty";

export type Commit =
  | { kind: "none" }
  | { kind: "send"; value: unknown }
  | { kind: "problem"; problem: string };

/** A draft with the spaces a person leaves around text taken off, as `parseDraft` reads it. */
function trimmed(draft: Draft): Draft {
  return Array.isArray(draft) ? draft : draft.trim();
}

/**
 * What leaving an answer does: nothing when it did not move, the one value to send, or what is
 * wrong with it (the client's checks, and a required answer that would be cleared, which the server
 * refuses too).
 *
 * `onItsWay` is the draft of this field already sent or waiting to be, when there is one: the
 * answer is then compared with that instead of the server's, so typing the old value back while a
 * save is in flight is sent as the change it is.
 */
export function commitOf(field: RecordField, draft: Draft, onItsWay?: Draft): Commit {
  if (!editable(field)) return { kind: "none" };
  const from = onItsWay ?? draftOf(field);
  if (sameValues(trimmed(draft), trimmed(from))) return { kind: "none" };
  const parsed = parseDraft(field, draft);
  if (!parsed.ok) return { kind: "problem", problem: parsed.problem };
  if (parsed.value === null && field.required) return { kind: "problem", problem: REQUIRED_PROBLEM };
  return { kind: "send", value: parsed.value };
}

/**
 * The drafts to keep after a save. A sent answer's draft goes only if it still reads as what was
 * sent: a rep who typed on while the request was away keeps what they typed, and it is sent when
 * they leave the field.
 */
export function draftsAfterSave(
  drafts: Readonly<Record<string, Draft>>,
  sent: Readonly<Record<string, Draft>>,
): Record<string, Draft> {
  const kept: Record<string, Draft> = {};
  for (const [key, draft] of Object.entries(drafts)) {
    const was = sent[key];
    if (was !== undefined && sameValues(draft, was)) continue;
    kept[key] = draft;
  }
  return kept;
}

// --- a request that did not save -------------------------------------------------------------------------

export const CONFLICT_TEXT = "Someone changed this at the same moment.";
export const NOT_SAVED_TEXT = "Not saved.";
export const FORBIDDEN_TEXT = "You cannot change this field.";
/** How long a save waits before its one second try after a 409. */
export const CONFLICT_RETRY_MS = 500;

export type FailureStep =
  /** Send the same request once more, after a pause. */
  | { kind: "retry"; afterMs: number }
  /** The server said which answers and why: `afterRefusal` reads it. */
  | { kind: "refused" }
  /** Every answer of the request is marked with this sentence. */
  | { kind: "failed"; text: string };

/**
 * What a failed request does next, by its status (null when no answer came at all). A 409 is two
 * writers at the same moment, which a second try half a second later usually clears; a 403 or a 422
 * names fields; anything else (the network, a 5xx) is simply not saved.
 */
export function failureStep(status: number | null, retried: boolean): FailureStep {
  if (status === 409) {
    return retried ? { kind: "failed", text: CONFLICT_TEXT } : { kind: "retry", afterMs: CONFLICT_RETRY_MS };
  }
  if (status === 403 || status === 422) return { kind: "refused" };
  return { kind: "failed", text: NOT_SAVED_TEXT };
}

export interface Refusal {
  /** What is wrong, under the field it is about, by key. Only keys of the request. */
  problems: Record<string, string>;
  /** The keys the server did not name, when it named others: sent again without those. */
  again: string[];
  /** When the server named none of the request's keys: the sentence for every one of them. */
  failed: string | null;
  /** The named keys that are no longer this person's to answer at all (the role may only read
   *  the field now, or the form has lost it). No value they type can be saved, so the store lets
   *  go of the answer once the rep has been told. */
  gone: string[];
}

/** A message `api.ts` made up from the status line because the answer carried no sentence. */
function wordless(message: string): boolean {
  return message.trim() === "" || /^\d{3}\b/.test(message);
}

function sentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/** What the server says of a key the form does not take, after the key and a space. */
const NOT_THEIRS = /^(is not a field of this form|is a platform field\b)/;

/**
 * A 403 or a 422 for a request of several answers. A save is all or nothing on the server, so one
 * refused answer holds back the others that travelled with it: the ones the server names keep their
 * reason under their field, and the rest are sent again without them.
 *
 * The server names a bad value as "key: why" (a 422, every bad one at once), a key the form does not
 * take as "key why" with no colon ("policy is not a field of this form", in the same "; " list), and
 * a field the role may only read as "You cannot change a, b" (a 403). Anything else names nobody,
 * and then the whole request is marked with the server's sentence, its keys turned into titles.
 */
export function afterRefusal(
  status: number,
  message: string,
  fields: readonly RecordField[],
  sentKeys: readonly string[],
): Refusal {
  const sent = new Set(sentKeys);
  const problems: Record<string, string> = {};
  const gone: string[] = [];
  for (const [key, why] of Object.entries(refusalProblems(message, fields).problems)) {
    if (sent.has(key)) problems[key] = why;
  }
  for (const part of message.split("; ")) {
    const [, key = "", why = ""] = /^([a-z][a-z0-9_]*) (.+)$/.exec(part) ?? [];
    if (!sent.has(key) || key in problems) continue;
    problems[key] = sentence(refusalText(part, fields));
    if (NOT_THEIRS.test(why)) gone.push(key);
  }
  const cannot = /^You cannot change (.+)$/.exec(message);
  for (const key of cannot?.[1]?.split(", ") ?? []) {
    if (!sent.has(key)) continue;
    problems[key] = FORBIDDEN_TEXT;
    gone.push(key);
  }
  if (Object.keys(problems).length > 0) {
    return { problems, again: sentKeys.filter((key) => !(key in problems)), failed: null, gone };
  }
  const failed = wordless(message)
    ? status === 403 ? FORBIDDEN_TEXT : NOT_SAVED_TEXT
    : sentence(refusalText(message, fields));
  return { problems: {}, again: [], failed, gone: [] };
}

// --- the header and the notice ---------------------------------------------------------------------------

/** How long "Saved" stays in the header after the last save. */
export const SAVED_SHOWN_MS = 2_000;

/**
 * How many of a lead's marked answers the header counts: those whose field the form on screen still
 * offers for an answer. A field an admin has since hidden, archived or made read-only, or one in a
 * folder the call does not draw, has no input to fix it in, and a count the rep can do nothing
 * about would stay in the header for good. `also` are the answers the pane draws beside its folders
 * (Send contract's "Needed for the contract", ACL-560), which do have one.
 */
export function countedMarks(
  marks: Readonly<Record<string, unknown>>,
  sections: readonly RecordSection[] | undefined,
  also: readonly string[] = [],
): number {
  if (sections === undefined) return 0;
  const open = new Set([
    ...callFormSections(sections).flatMap((s) => ownFields(s).filter(editable).map((f) => f.key)),
    ...also,
  ]);
  return Object.keys(marks).filter((key) => open.has(key)).length;
}

/**
 * The header's few words about saving. Answers that are not saved outrank everything, in the danger
 * tone, for as long as there are any; else "Saving…" while a request is away, then "Saved" for two
 * seconds, then nothing.
 */
export function saveSummary(state: {
  saving: boolean;
  /** How many answers are refused, failed or cannot be sent as typed. */
  failed: number;
  /** Milliseconds since the last save, or null when there has been none. */
  savedAgoMs: number | null;
}): { text: string; tone: "muted" | "danger" } | null {
  if (state.failed > 0) {
    return { text: `${state.failed} answer${state.failed === 1 ? "" : "s"} not saved`, tone: "danger" };
  }
  if (state.saving) return { text: "Saving…", tone: "muted" };
  if (state.savedAgoMs !== null && state.savedAgoMs >= 0 && state.savedAgoMs < SAVED_SHOWN_MS) {
    return { text: "Saved", tone: "muted" };
  }
  return null;
}

/** The one notice for answers a rep left unsaved on a lead they are no longer on. */
export function leftBehindText(name: string | null, titles: readonly string[]): string {
  const who = name ?? "this contact";
  const what = titles.length === 1 ? `1 answer for ${who} was` : `${titles.length} answers for ${who} were`;
  return `${what} not saved: ${titles.join(", ")}.`;
}
